/**
 * MetricWithUnitToggle — slider + input numérico + unit-toggle (R$/Km/L/…).
 *
 * Pattern recorrente nas calc legacy IBIOMA pros campos onde o usuário
 * pode informar o gasto em moeda, distância, litros, etc — escolhendo
 * unidade que conhece melhor. Compartilhado entre:
 *   - Etapa 1 (eletricidade): kWh|BRL ou L|BRL
 *   - Etapa 2 (combustíveis): BRL|km|L ou BRL|km|trips
 *
 * Range do slider varia por unidade ativa (kWh/L/BRL têm magnitudes
 * diferentes). Usuário pode digitar acima do range no input — o slider
 * só clampa quando arrasta o handle.
 */
import { useId } from 'react';
import type { FuelInput, FuelUnit } from '../api/types';
import { UnitToggle } from './UnitToggle';

interface Props {
  label: string;
  hint?: string;
  value: FuelInput;
  units: FuelUnit[];
  onChange: (v: FuelInput) => void;
}

// Range máximo razoável por unidade — pragmático pra eventos pequenos a
// festivais grandes. Acima disso o slider satura no handle direito, mas o
// número continua editável no input.
const MAX_BY_UNIT: Record<FuelUnit, number> = {
  BRL: 200000,
  km: 50000,
  L: 20000,
  kWh: 200000,
  trips: 5000,
};

const STEP_BY_UNIT: Record<FuelUnit, number> = {
  BRL: 10,
  km: 1,
  L: 1,
  kWh: 10,
  trips: 1,
};

export function MetricWithUnitToggle({ label, hint, value, units, onChange }: Props) {
  const id = useId();
  const activeUnit: FuelUnit = (value.unit || units[0]) as FuelUnit;

  const max = MAX_BY_UNIT[activeUnit] ?? 100000;
  const step = STEP_BY_UNIT[activeUnit] ?? 1;

  const setSafe = (raw: number) => {
    const n = Number.isNaN(raw) ? 0 : raw < 0 ? 0 : raw;
    onChange({ value: n, unit: activeUnit });
  };

  const fillPct = max > 0 ? Math.min(100, (value.value / max) * 100) : 0;

  return (
    <div className="py-3.5 border-b border-dashed border-areia/60 last:border-b-0">
      <label htmlFor={id} className="block text-[14px] text-ink leading-snug mb-3.5">
        {label}
      </label>
      <div className="grid grid-cols-[1fr_170px_auto] gap-3 items-center">
        {/* Track + fill + handle, pattern canônico do mockup .slider/.slider-handle */}
        <div className="relative h-1.5 rounded-full bg-cream cursor-pointer">
          <div
            className="absolute inset-y-0 left-0 rounded-full pointer-events-none"
            style={{
              width: `${fillPct}%`,
              background: 'linear-gradient(90deg, #9D764F, #4A4C39)',
            }}
          />
          <input
            id={id}
            type="range"
            min={0}
            max={max}
            step={step}
            value={Math.min(value.value, max)}
            onChange={(e) => setSafe(Number(e.target.value))}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label={label}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 size-[18px] rounded-full bg-floresta border-[3px] border-cream shadow-[0_2px_8px_rgba(74,76,57,0.32)] pointer-events-none"
            style={{ left: `calc(${fillPct}% - 9px)` }}
          />
        </div>
        <input
          type="number"
          inputMode="decimal"
          value={value.value}
          min={0}
          step={step}
          onChange={(e) => setSafe(Number(e.target.value))}
          className="block w-full rounded-lg border border-areia bg-cream/60 px-3 py-2 text-sm font-mono text-floresta text-right tabular-nums focus:outline-none focus:border-bronze focus:bg-cream"
        />
        <UnitToggle
          units={units}
          active={activeUnit}
          onChange={(u) => onChange({ value: value.value, unit: u })}
        />
      </div>
      {hint && <p className="text-2xs text-ink-subtle mt-1.5 font-mono">{hint}</p>}
    </div>
  );
}
